import { useEffect, useState } from 'react';
import { gql, useMutation, useQuery } from './urqlHooks';
import {
  dateFallsInPeriod,
  useOnAccountingPeriodChanged,
  useRefreshWhenVisible,
} from '../utils/accountingPeriodEvents';

// Queries
const GET_ACCOUNTING_PERIODS = gql`
  query GetAccountingPeriods($companyId: ID!, $year: Int!) {
    getAccountingPeriods(input: { companyId: $companyId, year: $year }) {
      _id
      companyId
      year
      periods {
        month
        year
        entriesStatus
        entriesClosedAt
        entriesClosedByUserId
        entriesClosedByUserEmail
        entriesOpenedAt
        entriesOpenedByUserId
        entriesOpenedByUserEmail
        reconciliationStatus
        reconciliationClosedAt
        reconciliationClosedByUserId
        reconciliationClosedByUserEmail
        reconciliationOpenedAt
        reconciliationOpenedByUserId
        reconciliationOpenedByUserEmail
        managementReportsStatus
        managementReportsClosedAt
        managementReportsClosedByUserId
        managementReportsClosedByUserEmail
        managementReportsOpenedAt
        managementReportsOpenedByUserId
        managementReportsOpenedByUserEmail
      }
      editionHistory {
        date
        userId
        userEmail
        month
        year
        periodType
        previousStatus
        newStatus
      }
      createdAt
      updatedAt
    }
  }
`;

const CHECK_PERIOD_LOCK = gql`
  query CheckPeriodLock($companyId: ID!, $date: DateTime!, $periodType: PeriodType!) {
    checkPeriodLock(input: { companyId: $companyId, date: $date, periodType: $periodType }) {
      isLocked
      message
      lockedMonth
      lockedYear
    }
  }
`;

// Mutations
const CLOSE_ACCOUNTING_PERIOD = gql`
  mutation CloseAccountingPeriod($companyId: ID!, $month: Int!, $year: Int!, $periodType: PeriodType!) {
    closeAccountingPeriod(input: { companyId: $companyId, month: $month, year: $year, periodType: $periodType }) {
      success
      message
      data {
        _id
        companyId
        year
        periods {
          month
          year
          entriesStatus
          entriesClosedAt
          entriesClosedByUserEmail
          reconciliationStatus
          reconciliationClosedAt
          reconciliationClosedByUserEmail
          managementReportsStatus
          managementReportsClosedAt
          managementReportsClosedByUserEmail
          managementReportsOpenedAt
          managementReportsOpenedByUserEmail
        }
      }
    }
  }
`;

const OPEN_ACCOUNTING_PERIOD = gql`
  mutation OpenAccountingPeriod($companyId: ID!, $month: Int!, $year: Int!, $periodType: PeriodType!) {
    openAccountingPeriod(input: { companyId: $companyId, month: $month, year: $year, periodType: $periodType }) {
      success
      message
      data {
        _id
        companyId
        year
        periods {
          month
          year
          entriesStatus
          entriesOpenedAt
          entriesOpenedByUserEmail
          reconciliationStatus
          reconciliationOpenedAt
          reconciliationOpenedByUserEmail
        }
      }
    }
  }
`;

// Custom hooks
export const useGetAccountingPeriods = (companyId, year) => {
  return useQuery({
    query: GET_ACCOUNTING_PERIODS,
    variables: { companyId, year },
    pause: !companyId || !year,
  });
};

export const useCheckPeriodLock = (companyId, date, periodType) => {
  const [result, reexecute] = useQuery({
    query: CHECK_PERIOD_LOCK,
    variables: { companyId, date, periodType },
    pause: !companyId || !date || !periodType,
    requestPolicy: 'network-only',
  });
  const [override, setOverride] = useState(null);

  const refresh = () => {
    if (!companyId || !date || !periodType) return;
    reexecute({ requestPolicy: 'network-only' });
  };

  useOnAccountingPeriodChanged(companyId, (message) => {
    if (!companyId || !date || !periodType) return;
    const sameMonth = message?.month && message?.year
      && dateFallsInPeriod(date, message.month, message.year);
    if (sameMonth && typeof message.opened === 'boolean') {
      setOverride({
        isLocked: !message.opened,
        message: message.opened ? '' : (result.data?.checkPeriodLock?.message || ''),
      });
    }
    refresh();
  }, periodType);

  useRefreshWhenVisible(Boolean(companyId && date && periodType), refresh);

  useEffect(() => {
    setOverride(null);
  }, [companyId, date, periodType]);

  useEffect(() => {
    if (!override || result.fetching || !result.data?.checkPeriodLock) return;
    if (result.data.checkPeriodLock.isLocked === override.isLocked) {
      setOverride(null);
    }
  }, [override, result.data, result.fetching]);

  if (!override) return [result, reexecute];

  return [{
    ...result,
    fetching: false,
    data: {
      ...result.data,
      checkPeriodLock: {
        ...(result.data?.checkPeriodLock || {}),
        ...override,
      },
    },
  }, reexecute];
};

export const useCloseAccountingPeriod = () => {
  return useMutation(CLOSE_ACCOUNTING_PERIOD);
};

export const useOpenAccountingPeriod = () => {
  return useMutation(OPEN_ACCOUNTING_PERIOD);
};

export {
  GET_ACCOUNTING_PERIODS,
  CHECK_PERIOD_LOCK,
  CLOSE_ACCOUNTING_PERIOD,
  OPEN_ACCOUNTING_PERIOD,
};

/**
 * Hasta qué mes ve ESTE usuario los reportes de gestión.
 *
 * Lo decide la pestaña "Reportes de Gestión" de /cierre_apertura_contable. Las
 * pantallas la usan para no ofrecer en el filtro de fechas un mes que el
 * reporte va a devolver vacío; el recorte de verdad lo hace el servidor.
 */
const MANAGEMENT_REPORTS_LIMIT = gql`
  query ManagementReportsLimit($companyId: String!) {
    managementReportsLimit(companyId: $companyId) {
      applies
      through
    }
  }
`;

export const useManagementReportsLimit = (companyId) => {
  const [result, reexecute] = useQuery({
    query: MANAGEMENT_REPORTS_LIMIT,
    variables: { companyId },
    pause: !companyId,
    requestPolicy: 'network-only',
  });

  useOnAccountingPeriodChanged(companyId, () => {
    if (!companyId) return;
    reexecute({ requestPolicy: 'network-only' });
  }, 'MANAGEMENT_REPORTS');

  return result;
};
